import { useState, useEffect, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import Lf23Loader from "../../components/Lf23Loader";
import { scaricaExcel, numeroPerExcel } from "../Admin/exportExcel";
import "./AnomalieGps.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const BASE = `${API_URL}/anomalie-gps`;

// Data locale del browser come stringa ISO (default dei date-picker)
const isoLocale = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const oggiIso = () => isoLocale(new Date());
// Default come nel resto del progetto (getDefaultDateRange): dal primo giorno del mese di 11 mesi fa a oggi
const inizioDefaultIso = () => {
    const oggi = new Date();
    return isoLocale(new Date(oggi.getFullYear(), oggi.getMonth() - 11, 1));
};

// separatore delle migliaia forzato: in it-IT il browser non lo mette sui numeri a 4 cifre
const fmtNum = (n, decimali = 0) => {
    if (n === null || n === undefined) return "—";
    const [intero, dec] = Math.abs(n).toFixed(decimali).split(".");
    const conPunti = intero.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${n < 0 ? "−" : ""}${conPunti}${dec ? `,${dec}` : ""}`;
};
const fmtGiorno = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");
const fmtGiornoOra = (iso) => (iso ? `${fmtGiorno(iso)} ${iso.slice(11, 16)}` : "—");
const fmtPeriodo = (dal, al) => (dal === al ? fmtGiorno(dal) : `${fmtGiorno(dal)} – ${fmtGiorno(al)}`);
const fmtDurata = (min) => {
    if (min < 1) return "<1 min";
    if (min < 60) return `${Math.round(min)} min`;
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    return m ? `${h} h ${m} min` : `${h} h`;
};
const plurale = (n, uno, molti) => `${fmtNum(n)} ${n === 1 ? uno : molti}`;
// "oggi" / "ieri" / "N giorni fa" rispetto alla data locale del browser
const fmtFa = (iso) => {
    if (!iso) return null;
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    const oggi = new Date();
    const giorni = Math.round((new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate()) - new Date(y, m - 1, d)) / 86400000);
    if (giorni <= 0) return "oggi";
    if (giorni === 1) return "ieri";
    return `${fmtNum(giorni)} giorni fa`;
};

// Cella su due righe: valore principale + dettaglio in grigio
const Doppia = ({ valore, sotto }) => (
    <div className="cella-doppia">
        <span>{valore}</span>
        {sotto && <span className="sotto">{sotto}</span>}
    </div>
);
const Targa = ({ nome }) => <span className="targa-chip">{nome}</span>;

const REGOLE_VIAGGIO = {
    km_gonfiati: {
        label: "Km gonfiati",
        descrizione: "Subito dopo il viaggio Webfleet ha corretto all'indietro il contachilometri: i km in più sono un errore della centralina"
    },
    velocita_impossibile: {
        label: "Distanza anomala",
        descrizione: "Distanza non compatibile con la durata del viaggio: la velocità media che ne risulta è molto oltre la massima registrata dalla centralina"
    }
};

const MOTIVI_CENTRALINA = {
    muta: {
        label: "Muta",
        descrizione: "Mezzo attivo in anagrafica che non manda segnali e non fa viaggi da giorni: centralina spenta, staccata o guasta, oppure mezzo fermo"
    },
    senza_centralina: {
        label: "Senza centralina",
        descrizione: "Mezzo attivo in anagrafica senza un dispositivo Webfleet associato (normale per rimorchi e semirimorchi)"
    }
};

const MOTIVI_TARGA = {
    gps_senza_targa: {
        label: "GPS senza targa",
        descrizione: "Non è una targa ma il numero di serie del dispositivo GPS: Webfleet usa questo nome quando il box non è abbinato a un mezzo targato"
    },
    targa_non_in_anagrafica: {
        label: "Targa non in anagrafica",
        descrizione: "È una targa, ma il mezzo non è (più) presente nell'anagrafica automezzi"
    },
    data_mancante: {
        label: "Data mancante",
        descrizione: "Il mezzo è in anagrafica ma senza data di immatricolazione"
    }
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// Riga di stato della mail giornaliera (stesso schema della mail dei percorsi da chiudere)
const statoNotifica = (info) => {
    if (!info) return null;
    const { jobAttivo, mailAbilitata, oraInvio, maxTentativi, erroriOggi, sospesoOggi, ultimo } = info;
    const quando = ultimo ? fmtGiornoOra(new Date(ultimo.avviato_il).toLocaleString("sv-SE")) : "";
    if (!jobAttivo) return { testo: "La mail giornaliera delle nuove anomalie non è attiva in questo ambiente." };
    if (sospesoOggi) {
        return { errore: true, testo: `⚠ Mail delle ${oraInvio} sospesa per oggi dopo ${maxTentativi} tentativi falliti${ultimo?.errore ? `: ${ultimo.errore}` : ""}. Si riprova domani.` };
    }
    if (!ultimo) return { testo: `Mail automatica ogni mattina alle ${oraInvio} con le sole anomalie nuove: nessun invio ancora eseguito.` };
    if (ultimo.esito === "errore") {
        return { errore: true, testo: `⚠ Ultimo invio fallito (${quando})${ultimo.errore ? `: ${ultimo.errore}` : ""}. Tentativi oggi ${erroriOggi}/${maxTentativi}, il sistema riprova ogni 30 minuti.` };
    }
    if (!ultimo.n_nuove) return { testo: `Mail automatica alle ${oraInvio} · ultimo controllo ${quando}: nessuna anomalia nuova, nessuna mail inviata.` };
    if (!mailAbilitata || (ultimo.errore || "").startsWith("dry-run")) {
        return { testo: `Mail automatica alle ${oraInvio} · ultimo giro ${quando} in prova (MAIL_ENABLED disattivo): la mail non è stata spedita.` };
    }
    return { testo: `Mail automatica alle ${oraInvio} · ultima inviata ${quando} a ${ultimo.destinatari || "—"} (${plurale(ultimo.n_nuove, "anomalia nuova", "anomalie nuove")}).` };
};

// Spiegazione in chiaro del singolo viaggio anomalo
const spiegaViaggio = (v) => {
    const base = `${fmtNum(v.km_registrati, 1)} km in ${fmtDurata(v.durata_min)}`;
    if (v.regole.includes("km_gonfiati")) {
        return `${base}, poi corretto da Webfleet di −${fmtNum(v.correzione_km, 1)} km`;
    }
    return `${base}: ${fmtNum(v.velocita_calcolata)} km/h di media contro una massima registrata di ${fmtNum(v.velocita_max_webfleet)} km/h`;
};

const Badge = ({ tipo, info }) => (
    <span className={`anomalie-gps-badge badge-${tipo}`} title={info?.descrizione}>
        {info?.label || tipo}
    </span>
);

const AnomalieGps = () => {
    const navigate = useNavigate();

    const [tab, setTab] = useState("viaggi");
    const [dal, setDal] = useState(inizioDefaultIso());
    const [al, setAl] = useState(oggiIso());
    const [fTarga, setFTarga] = useState([]);
    const [dati, setDati] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errore, setErrore] = useState(false);
    const [aperti, setAperti] = useState(() => new Set());
    const [notifica, setNotifica] = useState(null);
    const [inviando, setInviando] = useState(false);
    const [esitoInvio, setEsitoInvio] = useState(null);
    const [esportando, setEsportando] = useState(false);

    const caricaNotifica = () => {
        axios.get(`${BASE}/notifica`)
            .then(res => setNotifica(res.data))
            .catch(error => console.error("Errore nel caricamento stato notifica:", error));
    };

    // stato della mail giornaliera (una volta all'apertura, poi dopo ogni invio a mano)
    useEffect(() => { caricaNotifica(); }, []);

    const inviaOra = async () => {
        if (!window.confirm("Inviare ora la mail con le anomalie nuove? Quelle già segnalate non vengono ripetute.")) return;
        setInviando(true);
        setEsitoInvio(null);
        try {
            const { data } = await axios.post(`${BASE}/notifica/invia`, {}, { headers: authHeaders() });
            let testo;
            if (data.esito === "errore") testo = `Invio non riuscito${data.mail?.error ? `: ${data.mail.error}` : ""}.`;
            else if (!data.nuove) testo = "Nessuna anomalia nuova: nessuna mail inviata.";
            else if (data.mail?.status === "dry-run") testo = `${plurale(data.nuove, "anomalia nuova", "anomalie nuove")}: mail in prova (MAIL_ENABLED disattivo), non spedita.`;
            else testo = `Mail inviata con ${plurale(data.nuove, "anomalia nuova", "anomalie nuove")}.`;
            setEsitoInvio({ errore: data.esito === "errore", testo });
        } catch (error) {
            const msg = error.response?.data?.error || "errore di rete";
            setEsitoInvio({ errore: true, testo: `Invio non riuscito: ${msg}.` });
        } finally {
            setInviando(false);
            caricaNotifica();
        }
    };

    // I tre elenchi si caricano insieme a ogni cambio di periodo
    useEffect(() => {
        if (!dal || !al || dal > al) return undefined;
        // se l'utente cambia date velocemente, ignoro le risposte arrivate in ritardo
        let annullato = false;

        const fetchDati = async () => {
            setLoading(true);
            setErrore(false);
            try {
                const params = { params: { dal, al } };
                const [viaggi, centraline, targhe] = await Promise.all([
                    axios.get(`${BASE}/viaggi`, params),
                    axios.get(`${BASE}/centraline`, params),
                    axios.get(`${BASE}/targhe`, params)
                ]);
                if (!annullato) {
                    setDati({ viaggi: viaggi.data, centraline: centraline.data, targhe: targhe.data });
                    setAperti(new Set());
                }
            } catch (error) {
                console.error("Errore nel caricamento anomalie GPS:", error);
                if (!annullato) setErrore(true);
            } finally {
                if (!annullato) setLoading(false);
            }
        };

        fetchDati();
        return () => { annullato = true; };
    }, [dal, al]);

    // Opzioni del filtro targa: tutte le targhe presenti nei tre elenchi
    const targaOptions = useMemo(() => {
        if (!dati) return [];
        const set = new Set([
            ...dati.viaggi.perMezzo.map(m => m.targa),
            ...dati.centraline.centraline.map(c => c.targa),
            ...dati.targhe.targhe.map(t => t.nome_webfleet)
        ]);
        return [...set].sort().map(t => ({ value: t, label: t }));
    }, [dati]);

    // Filtro targa applicato in pagina: gli elenchi sono piccoli (decine di righe)
    const filtrati = useMemo(() => {
        if (!dati) return null;
        const scelte = new Set(fTarga.map(o => o.value));
        const passa = (t) => scelte.size === 0 || scelte.has(t);
        return {
            perMezzo: dati.viaggi.perMezzo.filter(m => passa(m.targa)),
            viaggi: dati.viaggi.viaggi.filter(v => passa(v.targa)),
            centraline: dati.centraline.centraline.filter(c => passa(c.targa)),
            targhe: dati.targhe.targhe.filter(t => passa(t.nome_webfleet))
        };
    }, [dati, fTarga]);

    const kpi = useMemo(() => {
        if (!filtrati) return null;
        return {
            viaggi: filtrati.viaggi.length,
            mezziViaggi: filtrati.perMezzo.length,
            kmNonAttendibili: filtrati.perMezzo.reduce((s, m) => s + m.km_eccesso, 0),
            mute: filtrati.centraline.filter(c => c.motivo === "muta").length,
            senzaCentralina: filtrati.centraline.filter(c => c.motivo === "senza_centralina").length,
            targhe: filtrati.targhe.length
        };
    }, [filtrati]);

    // Export Excel della tab aperta, con i filtri applicati. Il controller condiviso
    // (operazioniHrController.downloadFile) mette in grassetto l'ultima riga: è il Totale.
    const esporta = async () => {
        const periodo = `${dal}_${al}`;
        const FMT_KM = "#,##0.0";
        let config;
        if (tab === "viaggi") {
            const v = filtrati.viaggi;
            config = {
                tableName: "Anomalie GPS - Viaggi",
                filename: `anomalie_gps_viaggi_${periodo}.xlsx`,
                headers: ["Targa", "Tipologia", "Data", "Ora inizio", "Ora fine", "Durata (min)", "Km registrati", "Km non attendibili", "Anomalia", "Cosa è successo"],
                rows: [
                    ...v.map(x => [
                        x.targa, x.tipologia || "", fmtGiorno(x.data), x.ora_inizio, x.ora_fine,
                        numeroPerExcel(x.durata_min), numeroPerExcel(x.km_registrati), numeroPerExcel(x.km_eccesso),
                        x.regole.map(r => REGOLE_VIAGGIO[r]?.label || r).join(", "), spiegaViaggio(x)
                    ]),
                    [`Totale: ${plurale(v.length, "viaggio", "viaggi")}`, "", "", "", "", "",
                        numeroPerExcel(v.reduce((sum, x) => sum + x.km_registrati, 0)),
                        numeroPerExcel(v.reduce((sum, x) => sum + x.km_eccesso, 0)), "", ""]
                ],
                numFmt: ["", "", "", "", "", "0.0", FMT_KM, FMT_KM, "", ""]
            };
        } else if (tab === "centraline") {
            const c = filtrati.centraline;
            config = {
                tableName: "Anomalie GPS - Centraline",
                filename: `anomalie_gps_centraline_${oggiIso()}.xlsx`,
                headers: ["Targa", "Tipologia", "Stato", "Ultimo segnale", "Ultimo viaggio"],
                rows: [
                    ...c.map(x => [
                        x.targa, x.tipologia || "", MOTIVI_CENTRALINA[x.motivo]?.label || x.motivo,
                        x.ultimo_segnale ? fmtGiornoOra(x.ultimo_segnale) : "", x.ultimo_viaggio ? fmtGiorno(x.ultimo_viaggio) : ""
                    ]),
                    [`Totale: ${plurale(c.length, "mezzo", "mezzi")}`, "", "", "", ""]
                ]
            };
        } else {
            const t = filtrati.targhe;
            config = {
                tableName: "Anomalie GPS - Targhe",
                filename: `anomalie_gps_targhe_${periodo}.xlsx`,
                headers: ["Nome Webfleet", "Motivo", "Nota Webfleet", "Viaggi", "Km", "Dal", "Al"],
                rows: [
                    ...t.map(x => [
                        x.nome_webfleet, MOTIVI_TARGA[x.motivo]?.label || x.motivo, (x.nota_webfleet || "").replace(/\s+/g, " "),
                        x.num_viaggi, numeroPerExcel(x.km), fmtGiorno(x.primo_giorno), fmtGiorno(x.ultimo_giorno)
                    ]),
                    [`Totale: ${plurale(t.length, "nome", "nomi")}`, "", "",
                        t.reduce((sum, x) => sum + x.num_viaggi, 0), numeroPerExcel(t.reduce((sum, x) => sum + x.km, 0)), "", ""]
                ],
                numFmt: ["", "", "", "#,##0", FMT_KM, "", ""]
            };
        }

        setEsportando(true);
        try {
            await scaricaExcel({
                endpoint: "anomalie-gps/downloadExcel",
                tableName: config.tableName,
                filename: config.filename,
                datiDaStampare: {
                    headers: config.headers,
                    rows: config.rows,
                    formattazione: { numFmt: config.numFmt || [], freeze: true }
                }
            });
        } catch (error) {
            console.error("Errore durante l'export Excel:", error);
            alert("Impossibile generare il file Excel. Riprova più tardi.");
        } finally {
            setEsportando(false);
        }
    };

    // testo della regola + pulsante Esporta, in testa a ogni tab
    const intestazioneTab = (testo, righe) => (
        <div className="anomalie-gps-tab-head">
            <p className="anomalie-gps-regola">{testo}</p>
            <button type="button" className="anomalie-gps-export-btn" onClick={esporta} disabled={righe === 0 || esportando}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {esportando ? "Esporto…" : "Esporta"}
            </button>
        </div>
    );

    const toggleMezzo = (targa) => {
        setAperti(prev => {
            const next = new Set(prev);
            if (next.has(targa)) next.delete(targa); else next.add(targa);
            return next;
        });
    };

    const vuoto = (testo) => <p className="anomalie-gps-vuoto">{testo}</p>;

    const renderViaggi = () => (
        <>
            {intestazioneTab(
                <>
                    Viaggi con km non attendibili: <strong>km gonfiati</strong> (Webfleet ha poi corretto il contachilometri)
                    oppure <strong>distanza anomala</strong> (oltre {dati.viaggi.soglie.velocitaKmMin} km in un tempo non compatibile:
                    la velocità media risulta molto superiore alla massima registrata). Apri un mezzo per vedere i singoli viaggi;
                    l'export contiene tutti i viaggi.
                </>,
                filtrati.viaggi.length
            )}
            {filtrati.perMezzo.length === 0 ? vuoto("Nessun viaggio anomalo nel periodo.") : (
                <div className="anomalie-gps-table-wrap">
                    <table className="anomalie-gps-table">
                        <thead>
                            <tr>
                                <th className="col-toggle" />
                                <th>Targa</th>
                                <th>Tipologia</th>
                                <th>Anomalia</th>
                                <th className="num">Viaggi</th>
                                <th className="num" title="Km da togliere: la correzione di Webfleet, oppure l'intero viaggio se la velocità è impossibile">Km non attendibili</th>
                                <th>Periodo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtrati.perMezzo.map(m => {
                                const aperto = aperti.has(m.targa);
                                return (
                                    <Fragment key={m.targa}>
                                        <tr className={`riga-mezzo${aperto ? " aperta" : ""}`} onClick={() => toggleMezzo(m.targa)}>
                                            <td className="col-toggle">
                                                <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="9 6 15 12 9 18" />
                                                </svg>
                                            </td>
                                            <td><Targa nome={m.targa} /></td>
                                            <td className="tenue">{m.tipologia || "—"}</td>
                                            <td>{m.regole.map(r => <Badge key={r} tipo={r} info={REGOLE_VIAGGIO[r]} />)}</td>
                                            <td className="num">{fmtNum(m.num_viaggi)}</td>
                                            <td className="num km">{fmtNum(m.km_eccesso)} km</td>
                                            <td>
                                                <Doppia
                                                    valore={fmtPeriodo(m.primo_giorno, m.ultimo_giorno)}
                                                    sotto={`ultimo ${fmtFa(m.ultimo_giorno)}`}
                                                />
                                            </td>
                                        </tr>
                                        {aperto && (
                                            <tr className="riga-dettaglio">
                                                <td colSpan={7}>
                                                    <div className="dettaglio-titolo">
                                                        {plurale(m.num_viaggi, "viaggio anomalo", "viaggi anomali")} di {m.targa}
                                                    </div>
                                                    <table className="anomalie-gps-table interna">
                                                        <thead>
                                                            <tr>
                                                                <th>Data</th>
                                                                <th>Orario</th>
                                                                <th className="num">Km registrati</th>
                                                                <th className="num">Km non attendibili</th>
                                                                <th>Cosa è successo</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filtrati.viaggi.filter(v => v.targa === m.targa).map(v => (
                                                                <tr key={v.tripid}>
                                                                    <td>{fmtGiorno(v.data)}</td>
                                                                    <td className="tenue">{v.ora_inizio} – {v.ora_fine}</td>
                                                                    <td className="num">{fmtNum(v.km_registrati, 1)} km</td>
                                                                    <td className="num km">{fmtNum(v.km_eccesso, 1)} km</td>
                                                                    <td className="testo">{spiegaViaggio(v)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    const renderCentraline = () => {
        const s = dati.centraline.soglie;
        return (
            <>
                {intestazioneTab(
                    <>
                        Stato di oggi dei dispositivi Webfleet sui mezzi attivi in anagrafica: <strong>muta</strong> = nessun
                        segnale da più di {s.giorniSenzaSegnale} giorni e nessun viaggio da più di {s.giorniSenzaViaggi} (centralina
                        spenta, staccata o guasta, oppure mezzo fermo a lungo); <strong>senza centralina</strong> = nessun
                        dispositivo associato (normale per rimorchi e semirimorchi). Non dipende dal periodo selezionato.
                    </>,
                    filtrati.centraline.length
                )}
                {filtrati.centraline.length === 0 ? vuoto("Nessuna centralina da controllare.") : (
                    <div className="anomalie-gps-table-wrap">
                        <table className="anomalie-gps-table">
                            <thead>
                                <tr>
                                    <th>Targa</th>
                                    <th>Tipologia</th>
                                    <th>Stato</th>
                                    <th>Ultimo segnale</th>
                                    <th>Ultimo viaggio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtrati.centraline.map(c => (
                                    <tr key={c.targa}>
                                        <td><Targa nome={c.targa} /></td>
                                        <td className="tenue">{c.tipologia || "—"}</td>
                                        <td><Badge tipo={c.motivo} info={MOTIVI_CENTRALINA[c.motivo]} /></td>
                                        <td>
                                            {c.ultimo_segnale
                                                ? <Doppia valore={fmtGiornoOra(c.ultimo_segnale)} sotto={fmtFa(c.ultimo_segnale)} />
                                                : <span className="tenue">—</span>}
                                        </td>
                                        <td>
                                            {c.ultimo_viaggio
                                                ? <Doppia valore={fmtGiorno(c.ultimo_viaggio)} sotto={fmtFa(c.ultimo_viaggio)} />
                                                : <span className="tenue">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    };

    const renderTarghe = () => (
        <>
            {intestazioneTab(
                <>
                    Nomi registrati in Webfleet che non corrispondono a una targa in anagrafica: i loro km non si possono
                    attribuire a un mezzo. La nota è quella scritta sul dispositivo in Webfleet.
                </>,
                filtrati.targhe.length
            )}
            {filtrati.targhe.length === 0 ? vuoto("Nessuna targa non riconosciuta nel periodo.") : (
                <div className="anomalie-gps-table-wrap">
                    <table className="anomalie-gps-table">
                        <thead>
                            <tr>
                                <th>Nome Webfleet</th>
                                <th>Motivo</th>
                                <th>Nota Webfleet</th>
                                <th className="num">Viaggi</th>
                                <th className="num">Km</th>
                                <th>Periodo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtrati.targhe.map(t => (
                                <tr key={t.nome_webfleet}>
                                    <td><Targa nome={t.nome_webfleet} /></td>
                                    <td><Badge tipo={t.motivo} info={MOTIVI_TARGA[t.motivo]} /></td>
                                    <td className="nota">
                                        {t.nota_webfleet ? <span className="nota-testo">{t.nota_webfleet}</span> : <span className="tenue">—</span>}
                                    </td>
                                    <td className="num">{fmtNum(t.num_viaggi)}</td>
                                    <td className="num">{fmtNum(t.km)} km</td>
                                    <td>{fmtPeriodo(t.primo_giorno, t.ultimo_giorno)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    const TABS = [
        { key: "viaggi", label: "Viaggi anomali", conteggio: filtrati?.perMezzo.length },
        { key: "centraline", label: "Centraline", conteggio: filtrati?.centraline.length },
        { key: "targhe", label: "Targhe non riconosciute", conteggio: filtrati?.targhe.length }
    ];

    return (
        <div className="anomalie-gps">
            <div className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate("/automazione")}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <h2>Anomalie GPS mezzi</h2>
                        <p className="header-subtitle">
                            Dati Webfleet fuori logica da verificare: viaggi con km impossibili, centraline da controllare e
                            targhe non riconosciute.
                        </p>
                    </div>
                </div>
            </div>

            <div className="anomalie-gps-tabs">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        type="button"
                        className={`anomalie-gps-tab ${tab === t.key ? "active" : ""}`}
                        onClick={() => setTab(t.key)}
                    >
                        {t.label}
                        {t.conteggio !== undefined && <span className="conteggio">{t.conteggio}</span>}
                    </button>
                ))}
            </div>

            <div className="anomalie-gps-main">
                <div className="anomalie-gps-toolbar">
                    <div className="anomalie-gps-filter">
                        <label>Dal</label>
                        <DatePickerModal value={dal} onChange={(e) => setDal(e.target.value)} max={al} label="Data inizio" />
                    </div>
                    <div className="anomalie-gps-filter">
                        <label>Al</label>
                        <DatePickerModal value={al} onChange={(e) => setAl(e.target.value)} min={dal} label="Data fine" />
                    </div>
                    <div className="anomalie-gps-filter filtro-select">
                        <label>Targa</label>
                        <Select
                            isMulti
                            options={targaOptions}
                            value={fTarga}
                            onChange={(v) => setFTarga(v || [])}
                            classNamePrefix="acr-select"
                            placeholder="Tutte"
                            closeMenuOnSelect={false}
                            noOptionsMessage={() => "Nessuna targa"}
                        />
                    </div>
                </div>

                {notifica && (() => {
                    const riga = statoNotifica(notifica);
                    return (
                        <div className={`anomalie-gps-notifica${riga.errore ? " is-errore" : ""}`}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                <polyline points="22,6 12,13 2,6" />
                            </svg>
                            <span className="testo">
                                {riga.testo}
                                {esitoInvio && <span className={`esito${esitoInvio.errore ? " is-errore" : ""}`}> {esitoInvio.testo}</span>}
                            </span>
                            <button type="button" className="invia-ora" onClick={inviaOra} disabled={inviando}>
                                {inviando ? "Invio…" : "Invia ora"}
                            </button>
                        </div>
                    );
                })()}

                {errore && <p className="anomalie-gps-errore">Impossibile caricare le anomalie. Riprova più tardi.</p>}
                {!errore && !dati && loading && <Lf23Loader />}

                {!errore && dati && (
                    <div className="anomalie-gps-body">
                        {/* ricaricamento (cambio periodo): loader sopra i dati vecchi */}
                        {loading && (
                            <div className="anomalie-gps-loading-overlay">
                                <Lf23Loader testo={null} />
                            </div>
                        )}

                        <div className="anomalie-gps-kpi">
                            <div className="kpi">
                                <span className="kpi-label">Viaggi anomali</span>
                                <span className="kpi-valore">{fmtNum(kpi.viaggi)}</span>
                                <span className="kpi-dettaglio">{plurale(kpi.mezziViaggi, "mezzo", "mezzi")}</span>
                            </div>
                            <div className="kpi">
                                <span className="kpi-label">Km non attendibili</span>
                                <span className="kpi-valore rosso">{fmtNum(kpi.kmNonAttendibili)}</span>
                                <span className="kpi-dettaglio">nel periodo</span>
                            </div>
                            <div className="kpi">
                                <span className="kpi-label">Centraline mute</span>
                                <span className="kpi-valore">{fmtNum(kpi.mute)}</span>
                                <span className="kpi-dettaglio">
                                    {plurale(kpi.senzaCentralina, "mezzo", "mezzi")} senza centralina
                                </span>
                            </div>
                            <div className="kpi">
                                <span className="kpi-label">Targhe non riconosciute</span>
                                <span className="kpi-valore">{fmtNum(kpi.targhe)}</span>
                                <span className="kpi-dettaglio">km non attribuibili a un mezzo</span>
                            </div>
                        </div>

                        {tab === "viaggi" && renderViaggi()}
                        {tab === "centraline" && renderCentraline()}
                        {tab === "targhe" && renderTarghe()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnomalieGps;
